import React, { useEffect, useCallback, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';
import CmsAPI from '../../common/apis/CmsAPI';
import useInfiniteScroll from '../../common/hooks/useInfiniteScroll';
import { useTranslation } from 'react-i18next';
import { FILTER_ALL, FILTER_READ, FILTER_UNREAD } from '../constants/notificationMessages';
import { ToastNotification } from '../../engage/components/Toast/Toast';
import NotificationInToast from '../../engage/components/NotificationInToast/NotificationInToast';
import { useHistory } from 'react-router-dom';
import { useUserContext } from './UserContext';

function useMessage() {
  const { t } = useTranslation();
  const { user, logFirebaseEvent } = useUserContext();

  const [messages, setMessages] = useState([]);
  const [messageDetails, setMessageDetails] = useState({});
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [totalMessagesCount, setTotalMessagesCount] = useState(0);
  const [showReleaseAnnouncement, setShowReleaseAnnouncement] = useState(false);

  const [sortedMessages, setSortedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [nextPage, setNextPage] = useState(false);
  const [latestPublishDate, setLatestPublishDate] = useState();
  const [emptyList, setEmptyList] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(FILTER_ALL);

  const readSort = useMemo(() => {
    switch (selectedFilter.ROLE) {
      case FILTER_READ.ROLE:
        return true;
      case FILTER_UNREAD.ROLE:
        return false;
      default:
        return null;
    }
  }, [selectedFilter]);

  const [showNotificationDetail, setShowNotificationDetail] = useState(false);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const history = useHistory();

  const fetchMessagesOnInit = async (params = null) => {
    setMessageLoading(true);
    setLoading(true);
    const result = await CmsAPI.getMessages(params);

    if (result.length !== 0) {
      setNextPage(result?.messages?.length >= 50);
      const latestPublishDate = Math.min.apply(
        null,
        result?.messages?.map(item => item?.publishedAt)
      );
      setLatestPublishDate(latestPublishDate);
      setMessages(result?.messages);
      setUnreadMessagesCount(result?.count?.unread);
      setTotalMessagesCount(result?.count?.total);
      setEmptyList(false);
    } else {
      setEmptyList(true);
    }
    setMessageLoading(false);
    setLoading(false);
    setActionLoading(false);
  };

  useEffect(() => {
    sortedMessages.length && checkLocalStorageMessageId();
  }, [sortedMessages]);

  useEffect(() => {
    if (messages?.length === 0) {
      setEmptyList(true);
    }

    if (messages) {
      const sortedByDate = messages.sort(function(a, b) {
        a = new Date(a.publishedAt).getTime();
        b = new Date(b.publishedAt).getTime();
        return b - a;
      });
      if (showReleaseAnnouncement) {
        setSortedMessages(sortedByDate.filter(({ type }) => type === 9));
      } else {
        setSortedMessages(sortedByDate);
      }
    }
  }, [messages, showReleaseAnnouncement]);

  const getReadParams = useCallback(() => {
    var params = {};
    switch (selectedFilter.ROLE) {
      case FILTER_READ.ROLE:
        params.read = 1;
        break;
      case FILTER_UNREAD.ROLE:
        params.read = 0;
        break;
      default:
        break;
    }
    if (showReleaseAnnouncement) {
      params.type = 'release_announcement';
    }
    return params;
  }, [showReleaseAnnouncement, selectedFilter]);

  useEffect(() => {
    setNextPage(false);
    if (totalMessagesCount > 0) {
      setMessageLoading(true);
      const params = getReadParams();
      fetchMessages(params);
    } else {
      setEmptyList(true);
    }
  }, [selectedFilter]);

  const fetchMessages = async params => {
    const result = await CmsAPI.getMessages(params);
    if (result.length !== 0) {
      if (result?.messages?.length >= 50) {
        setNextPage(true);
      } else {
        setNextPage(false);
      }
      setTotalMessagesCount(result.count.total);
      const latest = Math.min.apply(
        null,
        result.messages.map(item => item?.publishedAt)
      );
      setLatestPublishDate(latest);
      setMessages(result.messages);
      setEmptyList(false);
    } else {
      setMessages([]);
      setEmptyList(true);
    }
    setMessageLoading(false);
    setLoading(false);
    setActionLoading(false);
  };

  const checkLocalStorageMessageId = useCallback(() => {
    const lastMessageId = localStorage.getItem('lastMessageId');
    if (typeof sortedMessages[0] !== 'undefined') {
      if (
        ((lastMessageId === 'undefined' || lastMessageId === null) && !sortedMessages[0]?.read) ||
        (lastMessageId < sortedMessages[0]?.id.toString() && !sortedMessages[0]?.read)
      ) {
        localStorage.setItem('lastMessageId', sortedMessages[0]?.id);
        ToastNotification({
          Child: (
            <NotificationInToast
              message={sortedMessages[0]}
              onClick={() => {
                logFirebaseEvent('toast_notif_cl', { message_id: sortedMessages[0].id });
                history.push({ pathname: '/eg/notifications', search: `id=${sortedMessages[0].id}` });
                setMessageDetails(sortedMessages[0]);
              }}
            />
          ),
          timeout: 5000,
          isMobileView,
        });
      }
    }
  }, [sortedMessages, user]);

  useEffect(() => {
    if (typeof messageDetails?.read !== 'undefined' && !messageDetails.read) {
      setUnreadMessagesCount(unreadMessagesCount - 1);
      readMessage(messageDetails);
    }
  }, [messageDetails]);

  const deleteMessage = async (messageDetails, read = false) => {
    await CmsAPI.updateMessage(messageDetails.id, { trashed: 1 });
    if (!messageDetails.read && !read) {
      setUnreadMessagesCount(unreadMessagesCount - 1);
    }
    setTotalMessagesCount(totalMessagesCount - 1);
    setMessages(messages.filter(({ id }) => id !== messageDetails.id));
    ToastNotification({
      message: `${t('messages.toast.successfully_deleted')}`,
      iconName: 'success',
      isMobileView: isMobileView,
    });
  };

  const readMessage = async messageDetails => {
    await CmsAPI.updateMessage(messageDetails.id, { read: 1 });
    //setSortedMessages(messages.filter(({id}) => id !== messageDetails.id))
    setMessages(messages.map(item => (item.id === messageDetails.id ? { ...item, read: true } : item)));
  };

  function filterObjectsById(objects, ids) {
    return objects.filter(obj => !ids.includes(obj.id));
  }

  const deleteAllMessages = useCallback(async () => {
    setActionLoading(true);
    await CmsAPI.batchMessages({ messages: '*', trashed: 1, readSort });
    setSelectedItems([]);
    if (readSort !== null) {
      setSelectedFilter(FILTER_ALL);
      fetchMessagesOnInit();
    } else {
      setMessages([]);
      setUnreadMessagesCount(0);
      setTotalMessagesCount(0);
      setEmptyList(true);
      setActionLoading(false);
    }
    ToastNotification({
      message: `${t('messages.toast.messages_successfully_deleted')}`,
      iconName: 'success',
      isMobileView: isMobileView,
    });
  }, [readSort]);

  const deleteSelectedMessages = useCallback(async () => {
    const selected = structuredClone(selectedItems);
    setSelectedItems([]);
    setActionLoading(true);

    const messagesToUpdate = selected.map(({ id }) => String(id));
    await CmsAPI.batchMessages({ messages: messagesToUpdate, trashed: 1 });

    const filteredReadMessages = messages.filter(obj => selected.find(({ id }) => id === obj.id) && !obj.read);
    setUnreadMessagesCount(unreadMessagesCount - filteredReadMessages.length);

    const filteredMessages = filterObjectsById(
      messages,
      selected.map(({ id }) => id)
    );

    const total = totalMessagesCount - selected.length;
    setTotalMessagesCount(total);
    setActionLoading(false);
    if (total === 0) {
      setEmptyList(true);
    }
    if (filteredMessages.length === 0) {
      if (nextPage) {
        setLoading(true);
        setMessageLoading(true);
        const params = getReadParams();
        fetchMessages(params);
      } else {
        if (selectedFilter.ROLE !== FILTER_ALL.ROLE && total > 0) {
          setSelectedFilter(FILTER_ALL);
        } else {
          setEmptyList(true);
          setMessages([]);
        }
      }
    } else {
      setMessages(filteredMessages);
    }
    ToastNotification({
      message: `${t('messages.toast.messages_successfully_deleted')}`,
      iconName: 'success',
      isMobileView: isMobileView,
    });
  }, [selectedItems]);

  const markAsReadAllMessages = async () => {
    setActionLoading(true);
    setSelectedItems([]);
    await CmsAPI.batchMessages({ messages: '*', read: 1 });
    setMessages(messages.map(item => ({ ...item, read: true })));
    setUnreadMessagesCount(0);
    setActionLoading(false);
  };

  const markAsReadSelectedMessages = useCallback(
    async (selectedMessages = null) => {
      setActionLoading(true);
      const selected = structuredClone(selectedMessages ? selectedMessages : selectedItems);
      setSelectedItems([]);

      // filter only unread messages and send only them to the backend
      const selectedUnreadMessageIds = selected.filter(({ read }) => !read).map(({ id }) => String(id));
      await CmsAPI.batchMessages({ messages: selectedUnreadMessageIds, read: 1 });

      const filteredReadMessages = messages.filter(obj => selected.find(({ id }) => id === obj.id) && !obj.read);
      setUnreadMessagesCount(unreadMessagesCount - filteredReadMessages.length);

      setMessages(messages.map(item => (selected.find(({ id }) => id === item.id) ? { ...item, read: true } : item)));
      setActionLoading(false);
    },
    [selectedItems]
  );

  const loadMore = async () => {
    try {
      const readParams = getReadParams();
      readParams.before = latestPublishDate - 1;
      const content = await CmsAPI.getMessages(readParams);
      if (content?.messages?.length) {
        setMessages(prevState => [...prevState, ...content.messages]);
        const latest = Math.min.apply(
          null,
          content.messages.map(item => item?.publishedAt)
        );
        setLatestPublishDate(latest);
      }
      if (content?.length === 0 || content?.messages?.length < 50) {
        setNextPage(false);
      } else {
        setNextPage(true);
      }
    } catch (e) {
      console.log(e.message);
    } finally {
      setLoading(false);
      setMessageLoading(false);
      setActionLoading(false);
    }
  };

  const infiniteScroll = useCallback(async () => {
    console.log('=== load more triggered New Page:', nextPage);
    if (!nextPage) return;
    setLoading(true);
    loadMore();
  }, [latestPublishDate, nextPage, setMessages, setLoading]);

  const scrollRef = useInfiniteScroll({
    hasNextPage: nextPage,
    loading,
    onLoadMore: infiniteScroll,
    threshold: 150,
  });

  const setMessageDetailsById = id => {
    const idAsNumber = Number(id);
    setMessageDetails(messages.find(item => item.id === idAsNumber));
  };

  const resetContext = () => {
    setMessages([]);
    setMessageDetails({});
    setUnreadMessagesCount();
    setSortedMessages([]);
    setLoading(false);
    setMessageLoading(false);
    setSelectedItems([]);
    setSelectedFilter(FILTER_ALL);
    setShowNotificationDetail(false);
  };

  useEffect(() => {
    if (messages?.length && user === null) {
      resetContext();
    }
  }, [user]);

  return useMemo(
    () => ({
      loading,
      messageLoading,
      actionLoading,
      scrollRef,
      messageDetails,
      messages,
      unreadMessagesCount,
      sortedMessages,
      setMessageDetails,
      deleteMessage,
      selectedFilter,
      setSelectedFilter,
      selectedItems,
      setSelectedItems,
      resetContext,
      showNotificationDetail,
      setShowNotificationDetail,
      deleteSelectedMessages,
      deleteAllMessages,
      markAsReadSelectedMessages,
      markAsReadAllMessages,
      totalMessagesCount,
      fetchMessagesOnInit,
      setEmptyList,
      emptyList,
      setShowReleaseAnnouncement,
      showReleaseAnnouncement,
      setMessageDetailsById,
    }),
    [
      loading,
      messageLoading,
      actionLoading,
      scrollRef,
      messageDetails,
      messages,
      unreadMessagesCount,
      sortedMessages,
      setSortedMessages,
      selectedFilter,
      setSelectedFilter,
      deleteMessage,
      selectedItems,
      setSelectedItems,
      resetContext,
      showNotificationDetail,
      setShowNotificationDetail,
      deleteSelectedMessages,
      deleteAllMessages,
      markAsReadSelectedMessages,
      markAsReadAllMessages,
      totalMessagesCount,
      fetchMessagesOnInit,
      setEmptyList,
      emptyList,
      setShowReleaseAnnouncement,
      showReleaseAnnouncement,
      setMessageDetailsById,
    ]
  );
}

export const useMessageContext = constate(useMessage);

const MessageContext = {
  Provider: useMessageContext.Provider,
};

export default MessageContext;
